import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Menu, Tooltip } from '@mantine/core';
import { IconReceipt } from '@tabler/icons-react';
import {
  LeadViewExtensions,
  type LeadViewPayload,
} from '@ayocore/erp/crm/client/components/Leads/views/LeadViewExtensions';
import { createDealFromLead } from '@ayocore/elita/client/rpc';
import { notifications } from '@mantine/notifications';
import logger from '@ayocore/platform/common/helpers/logger';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { DealView } from '@ayocore/erp/crm/client/components/Deals/views/DealView';
import { CRM_LEAD_TYPES } from '@ayocore/erp/crm/constants';

/** Типы лидов, для которых elita разрешает создавать сделку из карточки. */
const ELITA_LEAD_TYPE_SLUGS: ReadonlySet<string> = new Set([
  CRM_LEAD_TYPES.ACQUISITION,
  CRM_LEAD_TYPES.RETENTION,
]);

/**
 * Пункт меню «Создать сделку» в карточке лида.
 *
 * - Показывается только для лидов воронок Elita (привлечение/удержание).
 * - Всегда «Создать сделку» — серверная функция createDealFromLead сама
 *   решает: первое создание (привяжет deal_id к лиду) или повторное
 *   на того же партнёра (lead.deal_id не трогает, см. сервер).
 * - Гард для повторного создания: если у лида уже есть сделка, но не
 *   указан партнёр — пункт disabled с подсказкой; чтобы создать вторую
 *   сделку, юзеру нужно сначала проставить партнёра в лиде.
 * - Открывает Drawer созданной сделки.
 */
function CreateElitaDealMenuItem({ lead, leadTypeSlug, reload }: LeadViewPayload) {
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const dealDrawer = useDrawer(DealView, {
    openInTab: props => navigate(`/crm/deals/${props.dealId}`),
  });

  if (!leadTypeSlug || !ELITA_LEAD_TYPE_SLUGS.has(leadTypeSlug)) return null;

  const needsPartnerForRepeat = Boolean(lead.deal_id) && !lead.partner_id;
  const disabledHint = 'Чтобы добавить вторую сделку, необходимо указать партнёра в этом лиде.';

  const handleCreate = async () => {
    try {
      setCreating(true);
      const result = await createDealFromLead({ lead_id: lead.id });
      notifications.show({
        title: 'Сделка создана',
        message: `Сделка №${result.deal_number} создана из лида`,
        color: 'green',
      });
      reload();
      dealDrawer.open({ dealId: result.deal_id, isModal: true, initialTab: 'rows' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка создания сделки';
      notifications.show({ title: 'Ошибка', message, color: 'red' });
      logger.error(err, '[elita] createDealFromLead failed');
    } finally {
      setCreating(false);
    }
  };

  const item = (
    <Menu.Item
      leftSection={<IconReceipt size={14} />}
      onClick={() => void handleCreate()}
      disabled={creating || needsPartnerForRepeat}
      color='teal'
    >
      {creating ? 'Создание...' : 'Создать сделку'}
    </Menu.Item>
  );

  // Tooltip над disabled Menu.Item — оборачиваем в <span>, иначе Tooltip
  // не получит pointer-events с disabled-элемента.
  if (needsPartnerForRepeat) {
    return (
      <Tooltip label={disabledHint} withArrow multiline w={260} position='left'>
        <span style={{ display: 'block' }}>{item}</span>
      </Tooltip>
    );
  }

  return item;
}

/**
 * Регистрирует расширения elita для LeadView.
 * Вызывается один раз из bootstrap.ts.
 */
export function registerElitaLeadViewExtensions(): void {
  LeadViewExtensions.menuActions.register(payload => <CreateElitaDealMenuItem {...payload} />);
}
